import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Plus, Pencil, Trash2, Mail, MapPin, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [formData, setFormData] = useState({
    customer_number: '',
    company_name: '',
    email: '',
    address: '',
    notes: '',
    cc_emails: ['']
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Filter out empty CC emails
      const cleanedData = {
        ...formData,
        cc_emails: formData.cc_emails.filter(email => email.trim() !== '')
      };

      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, cleanedData);
        toast.success('Kunde erfolgreich aktualisiert');
      } else {
        await axios.post(`${API}/customers`, cleanedData);
        toast.success('Kunde erfolgreich erstellt');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern des Kunden');
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      customer_number: customer.customer_number,
      company_name: customer.company_name,
      email: customer.email,
      address: customer.address,
      notes: customer.notes || '',
      cc_emails: customer.cc_emails && customer.cc_emails.length > 0 ? customer.cc_emails : ['']
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/customers/${customerToDelete.id}`);
      toast.success('Kunde erfolgreich gelöscht');
      fetchCustomers();
    } catch (error) {
      toast.error('Fehler beim Löschen des Kunden');
    } finally {
      setDeleteDialogOpen(false);
      setCustomerToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_number: '',
      company_name: '',
      email: '',
      address: '',
      notes: '',
      cc_emails: ['']
    });
    setEditingCustomer(null);
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  const handleAddCcEmail = () => {
    setFormData({ ...formData, cc_emails: [...formData.cc_emails, ''] });
  };

  const handleRemoveCcEmail = (index) => {
    const newEmails = formData.cc_emails.filter((_, i) => i !== index);
    setFormData({ ...formData, cc_emails: newEmails.length > 0 ? newEmails : [''] });
  };

  const handleCcEmailChange = (index, value) => {
    const newEmails = [...formData.cc_emails];
    newEmails[index] = value;
    setFormData({ ...formData, cc_emails: newEmails });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-slate-500">Lädt...</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:p-12 space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-slate-900" data-testid="customers-title">
            Kunden
          </h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Verwalten Sie Ihre Kundendaten</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-primary hover:bg-[#D62839] shadow-none" data-testid="create-customer-button" onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" />
              Neuer Kunde
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingCustomer ? 'Kunde bearbeiten' : 'Neuer Kunde'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="customer_number">Kundennummer</Label>
                <Input
                  id="customer_number"
                  value={formData.customer_number}
                  onChange={(e) => setFormData({ ...formData, customer_number: e.target.value })}
                  required
                  data-testid="customer-number-input"
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_name">Firmenname</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  required
                  data-testid="company-name-input"
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="customer-email-input"
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Adresse</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                  data-testid="customer-address-input"
                  className="rounded-sm"
                  rows={3}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notizen (optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="customer-notes-input"
                  className="rounded-sm"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>CC-Empfänger für Rechnungen (optional)</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleAddCcEmail}
                    className="h-7 text-xs rounded-sm"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Hinzufügen
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.cc_emails.map((email, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="email@beispiel.de"
                        value={email}
                        onChange={(e) => handleCcEmailChange(index, e.target.value)}
                        className="rounded-sm text-sm"
                      />
                      {formData.cc_emails.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveCcEmail(index)}
                          className="h-9 w-9 rounded-sm flex-shrink-0"
                        >
                          <X className="w-4 h-4 text-slate-600" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <p className="text-xs text-slate-500">
                    Diese E-Mail-Adressen werden automatisch als CC beim Rechnungsversand hinzugefügt.
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleDialogClose} className="rounded-sm">
                  Abbrechen
                </Button>
                <Button type="submit" className="rounded-sm bg-primary hover:bg-[#D62839]" data-testid="save-customer-button">
                  {editingCustomer ? 'Aktualisieren' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Customers Grid */}
      {customers.length === 0 ? (
        <Card className="border-slate-200 shadow-none rounded-sm">
          <CardContent className="py-12">
            <div className="text-center text-slate-400">
              <p>Keine Kunden vorhanden</p>
              <p className="text-sm mt-1">Erstellen Sie Ihren ersten Kunden</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {customers.map((customer) => (
            <Card key={customer.id} className="border-slate-200 shadow-none rounded-sm hover:shadow-sm transition-shadow" data-testid="customer-card">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg">{customer.company_name}</CardTitle>
                    <p className="font-mono text-xs text-slate-500 mt-1">#{customer.customer_number}</p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(customer)}
                      data-testid={`edit-customer-${customer.id}`}
                      className="h-8 w-8 rounded-sm hover:bg-slate-100"
                    >
                      <Pencil className="w-4 h-4 text-slate-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        setCustomerToDelete(customer);
                        setDeleteDialogOpen(true);
                      }}
                      data-testid={`delete-customer-${customer.id}`}
                      className="h-8 w-8 rounded-sm hover:bg-slate-100"
                    >
                      <Trash2 className="w-4 h-4 text-slate-600" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-2 text-sm">
                  <Mail className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600 break-all">{customer.email}</span>
                </div>
                <div className="flex items-start gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-600">{customer.address}</span>
                </div>
                {customer.notes && (
                  <div className="pt-2 border-t border-slate-200">
                    <p className="text-xs text-slate-500">{customer.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Kunde löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Kunden "{customerToDelete?.company_name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-sm bg-primary hover:bg-[#D62839]">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Customers;